// Inspecteur — Partner-role DETAIL drill-in (design-vs-build Batch 4; the audit's top gap). The partner
// register was list-only; a role now opens to show its party, its Art.28 DPA agreements, and the
// Verwerkingen it is bound to (as processor / recipient / joint-controller). Backend: GET
// /partner-roles/:id/detail. RLS-scoped; gated partner_relationship.read.
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { api } from "../../lib/api";
import { Can } from "../../lib/rbac";
import { useI18n } from "../../lib/i18n";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ChevronLeft, Plus } from "lucide-react";
import { DetailShell, DetailFields, DetailPanel, dv, type DRow } from "./_detail";

export function PartnerRoleDetailRoute() {
  const { t } = useI18n();
  const { id } = useParams({ from: "/partner-roles/$id" });
  const q = useQuery({ queryKey: ["partner-role-detail", id], queryFn: () => api.getPartnerRoleDetail(id) as Promise<{ role: DRow; agreements: DRow[]; verwerkingen: DRow[] }> });
  const role = (q.data as { role?: DRow } | undefined)?.role;
  const back = <Link to="/partners" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ChevronLeft className="size-4" /> {t("partner.detail.back")}</Link>;
  const kindLabel = (k: unknown) => { const key = `partner.detail.kind.${String(k)}`; const l = t(key); return l === key ? String(k) : l; };
  return (
    <DetailShell back={back} title={dv(role?.partyName)} {...(role ? { sub: dv(role.role) } : {})} q={q as never}>
      {(d) => { const r = d.role as DRow; return (<>
        <DetailFields items={[[t("partner.f.role"), r.role], [t("partner.f.partyKind"), r.partyKind], [t("partner.f.country"), r.partyCountry], [t("partner.f.contactName"), r.contactName], [t("partner.f.contactEmail"), r.contactEmail]]} />
        <div>
          <DetailPanel title={t("partner.detail.agreements")} rows={(d.agreements as DRow[]) ?? []} empty={t("partner.detail.noAgreements")}
            renderRow={(a) => (<>
              <span className="min-w-0 truncate font-medium">{dv(a.agreementReference)}</span>
              <span className="flex shrink-0 items-center gap-2">
                {Boolean(a.isSccBased) && <Badge variant="outline">SCC</Badge>}
                <span className="text-xs text-muted-foreground">{a.signedAt ? new Date(String(a.signedAt)).toLocaleDateString() : t("partner.detail.unsigned")}</span>
              </span>
            </>)} />
          <Can perm="partner_relationship.write"><div className="mt-2"><AddAgreement roleId={id} /></div></Can>
        </div>
        <DetailPanel title={t("partner.detail.verwerkingen")} rows={(d.verwerkingen as DRow[]) ?? []} empty={t("partner.detail.noVerwerkingen")}
          renderRow={(v) => (<>
            <Link to="/verwerkingen/$id" params={{ id: String(v.id) }} className="font-medium text-primary hover:underline">{dv(v.name)}</Link>
            <Badge variant="secondary">{kindLabel(v.kind)}</Badge>
          </>)} />
      </>); }}
    </DetailShell>
  );
}

/** Create an Art.28 processor agreement (DPA) for this partner role — completes the partner authoring path. */
function AddAgreement({ roleId }: { roleId: string }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [ref, setRef] = useState("");
  const [scc, setScc] = useState(false);
  const create = useMutation({
    mutationFn: () => api.createProcessorAgreement({ processorPartnerRoleId: roleId, agreementReference: ref.trim() || undefined, isSccBased: scc } as Parameters<typeof api.createProcessorAgreement>[0]),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["partner-role-detail", roleId] }); setOpen(false); setRef(""); setScc(false); },
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="xs" variant="outline"><Plus aria-hidden className="size-3.5" /> {t("partner.detail.addAgreement")}</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>{t("partner.detail.addAgreement")}</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <div className="grid gap-1.5">
            <Label htmlFor="dpa-ref">{t("partner.detail.agreementRef")}</Label>
            <Input id="dpa-ref" value={ref} onChange={(e) => setRef(e.target.value)} placeholder="DPA-2026-001" />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={scc} onChange={(e) => setScc(e.target.checked)} /> {t("partner.detail.sccBased")}
          </label>
          {create.isError && <p className="text-sm text-destructive">{(create.error as Error).message}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
          <Button onClick={() => create.mutate()} disabled={create.isPending}>{create.isPending ? t("crud.creating") : t("common.create")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
